import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import { toast } from "../../helper/common";
import BillingDetailsFields from "../BillingDetailsFields";
import { Button, Modal } from "../design";

const EditBillingModal = ({ show, onClose }) => {
  const { _t } = useTranslator();
  const { updateWorkspaceBilling } = useContext(LandLordContext);
  const { user, loadUserTenant } = useContext(AuthContext);

  const saved = user?.current_workspace?.billing_details ?? {};

  const snapshot = {
    billing_type: saved.billing_type || "business",
    company_name: saved.company_name || "",
    full_name: saved.full_name || "",
    tax_id: saved.tax_id || "",
    country: saved.country || "",
    city: saved.city || "",
    address: saved.address || "",
    postal_code: saved.postal_code || "",
  };

  const [billingType, setBillingType] = useState(snapshot.billing_type);
  const [billingData, setBillingData] = useState({
    company_name: snapshot.company_name,
    full_name: snapshot.full_name,
    tax_id: snapshot.tax_id,
    country: snapshot.country,
    city: snapshot.city,
    address: snapshot.address,
    postal_code: snapshot.postal_code,
  });
  const [saving, setSaving] = useState(false);

  const {
    company_name,
    full_name,
    tax_id,
    country,
    city,
    address,
    postal_code,
  } = billingData;

  const baseComplete =
    [country, city, address].every((v) => v.trim() !== "") &&
    postal_code.trim().length === 5;

  const isComplete =
    billingType === "business"
      ? baseComplete && company_name.trim() !== "" && tax_id.trim() !== ""
      : baseComplete && full_name.trim() !== "";

  const hasChanges =
    billingType !== snapshot.billing_type ||
    Object.keys(billingData).some((key) => billingData[key] !== snapshot[key]);

  const canSave = isComplete && hasChanges && !saving;

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateWorkspaceBilling({
        billing_type: billingType,
        ...(billingType === "business"
          ? { company_name, tax_id }
          : { full_name }),
        country,
        city,
        address,
        postal_code,
      });
      await loadUserTenant("landlord");
      toast.success(_t("billingDetailsSaved"));
      onClose?.();
    } catch (err) {
      console.log(err);
      toast.error(_t("errorWhileSavingBillingDetails"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      size="md"
      align="start"
      title={_t("editBillingInformation")}
      description={_t("billingDetailsDescription")}
      actionsLayout="end"
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            {_t("cancel")}
          </Button>
          <Button
            size="lg"
            onClick={handleSave}
            disabled={!canSave}
            loading={saving}
          >
            {_t("save")}
          </Button>
        </>
      }
    >
      <div className="workspace-billing-form">
        <BillingDetailsFields
          billingType={billingType}
          onBillingTypeChange={setBillingType}
          billingData={billingData}
          onFieldChange={(key, value) =>
            setBillingData((d) => ({ ...d, [key]: value }))
          }
        />
      </div>
    </Modal>
  );
};

export default EditBillingModal;
